/**
 * What a room is made of, part by part — its floor, countertops, cabinets,
 * appliances, fixtures, walls and ceiling — for the line under its name at
 * the top of the report: what an asset manager reads first (the user,
 * 2026-10-16: "flooring type, countertop type (granite, etc), cabinetry type,
 * etc."). Read from the materials the inspection named: a part the recording
 * did not see, or whose material the model could not name, is left out rather
 * than guessed. Pure.
 */
const { materialKindFor, APPLIANCE_DEVICE_TYPES } = require('./vocabulary');
const { codeLabel } = require('./inspectionLabels');

const isNamed = (cls) => Boolean(cls) && cls !== 'other' && cls !== 'unknown';
const classOf = (doc) => doc.material && doc.material.class;
const typeOf = (doc) => (doc.anchor && doc.anchor.type) || '';

// Plurals the trailing-s rule gets wrong.
const PLURALS = { 'Washer and dryer': 'Washers and dryers' };
/** A part named for how many there are: "Countertop", "Countertops". */
const counted = (label, n) => (n > 1 ? PLURALS[label] || `${label}s` : label);

/** Material classes in words, most common first (ties as first seen), each once. */
function materialsOf(classes) {
  const counts = new Map();
  for (const cls of classes.filter(isNamed)) {
    counts.set(cls, (counts.get(cls) || 0) + 1);
  }
  return [...counts]
    .map(([cls, n], seen) => ({ cls, n, seen }))
    .sort((a, b) => b.n - a.n || a.seen - b.seen)
    .map(({ cls }) => codeLabel(cls));
}

/** `{ part, materials }` from the material classes of its docs, or null when none is named. */
function part(name, classes) {
  const materials = materialsOf(classes);
  return materials.length ? { part: name, materials } : null;
}

/** Docs grouped by their object type's words, in the order first seen: [[label, docs]]. */
function byType(docs) {
  const groups = new Map();
  for (const d of docs) {
    const label = codeLabel(typeOf(d) || 'object');
    groups.set(label, [...(groups.get(label) || []), d]);
  }
  return [...groups];
}

/** A wall's second finish's place in words ("Backsplash"), or null. */
function placeOf(doc) {
  const second = doc.material && doc.material.second;
  return second && second.place && second.place !== 'other'
    ? codeLabel(second.place)
    : null;
}

// The devices the recording saw, by the part of the room they finish: what an
// asset manager reads as dated (polished brass trim, almond plates) or current.
const DEVICE_PARTS = {
  faucets: ['faucet', 'shower-valve', 'tub-spout', 'shower-head'],
  lighting: ['ceiling-light', 'wall-light', 'emergency-light', 'ceiling-fan'],
  accessories: ['towel-bar', 'towel-ring', 'towel-hook', 'toilet-paper-holder'],
  plates: ['outlet', 'gfci-outlet', 'outlet-240v', 'switch', 'dimmer', '3way'],
};

/**
 * The room's parts. `surfaces`: the floor, ceiling and wall docs the recording
 * saw; `objects`: the docs of its fixed items the recording saw (never one
 * the contractor said is not built in); `devices`: the devices it saw,
 * `{ type, finish }` (never a duplicate or one the contractor hid). →
 * [{ part, materials: [label] }] in the order a reader wants them: floor,
 * countertops, cabinets, appliances (a microwave the scan missed among them),
 * fixtures, faucets and trim, lighting, accessories, fireplace, doors,
 * windows, walls, what is on the walls (a backsplash), ceiling, outlets and
 * switches. A door's or window's docs ride `objects` (kind `door` / `window`).
 */
function roomMakeup(surfaces, objects, devices = []) {
  const ofKind = (kind) => surfaces.filter((d) => d.kind === kind);
  const isOpening = (d) => d.kind === 'door' || d.kind === 'window';
  const objectsOf = (kind) =>
    objects.filter(
      (d) => !isOpening(d) && materialKindFor('object', typeOf(d)) === kind
    );
  const openingsOf = (kind) => objects.filter((d) => d.kind === kind);
  const named = (label, docs) =>
    part(counted(label, docs.length), docs.map(classOf));

  const devicesOf = (types) => devices.filter((d) => types.includes(d.type));
  const finishes = (list) => list.map((d) => d.finish);
  // the appliances: the fixed ones, and those only the recording found
  const appliances = [
    ...objectsOf('appliance').map((d) => ({
      type: typeOf(d),
      cls: classOf(d),
    })),
    ...devicesOf(APPLIANCE_DEVICE_TYPES).map((d) => ({
      type: d.type,
      cls: d.finish,
    })),
  ];
  const walls = ofKind('wall-face');
  // one finish across several walls is one backsplash
  const onWalls = [...new Set(walls.map(placeOf).filter(Boolean))].map(
    (place) =>
      part(
        place,
        walls
          .filter((d) => placeOf(d) === place)
          .map((d) => d.material.second.class)
      )
  );
  return [
    named('Floor', ofKind('floor')),
    named('Countertop', objectsOf('countertop')),
    named('Cabinet', objectsOf('cabinetry')),
    part(
      appliances.length === 1 ? codeLabel(appliances[0].type) : 'Appliances',
      appliances.map((a) => a.cls)
    ),
    ...byType(objectsOf('fixture')).map(([label, docs]) => named(label, docs)),
    part('Faucets and trim', finishes(devicesOf(DEVICE_PARTS.faucets))),
    part('Lighting', finishes(devicesOf(DEVICE_PARTS.lighting))),
    part('Accessories', finishes(devicesOf(DEVICE_PARTS.accessories))),
    named('Fireplace', objectsOf('fireplace')),
    named('Door', openingsOf('door')),
    named('Window', openingsOf('window')),
    part('Walls', walls.map(classOf)),
    ...onWalls,
    named('Ceiling', ofKind('ceiling')),
    part('Outlets and switches', finishes(devicesOf(DEVICE_PARTS.plates))),
  ].filter(Boolean);
}

module.exports = { roomMakeup };
